import React, { useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, MapPin, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HeroSection = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("");

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Construct search query parameters
    const params = new URLSearchParams();
    if (searchQuery) params.append("q", searchQuery);
    if (location) params.append("location", location);
    
    // Navigate to jobs page with search parameters
    navigate(`/jobs?${params.toString()}`);
  };

  return (
    <section className="relative min-h-[85vh] flex items-center bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center lg:text-left"
          >
            <motion.h1
              variants={itemVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
            >
              Find Your Dream Job Today
            </motion.h1>
            <motion.p
              variants={itemVariants}
              className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto lg:mx-0"
            >
              Discover thousands of job opportunities with all the information you
              need. Its your future.
            </motion.p>

            <div className="mt-8 text-gray-300 text-sm flex items-center justify-center lg:justify-start space-x-6">
              <span className="flex items-center">
                <Briefcase className="h-4 w-4 mr-2" />
                10K+ Jobs
              </span>
              <span className="flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                150+ Cities
              </span>
              <span className="flex items-center">
                <Search className="h-4 w-4 mr-2" />
                1M+ Searches
              </span>
            </div>
          </motion.div>

          {/* Search Box */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="lg:w-full w-full"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-white/20"
            >
              <h2 className="text-2xl font-bold mb-6 text-white">
                Find Your Next Opportunity
              </h2>

              <form onSubmit={handleSearch} className="space-y-4">
                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  className="relative group"
                >
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 group-hover:text-white/80 transition-colors" />
                  <input
                    type="text"
                    placeholder="Job title, skills or keywords"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white/10 border-2 border-white/20 rounded-xl focus:border-white/40 focus:ring-4 focus:ring-white/10 outline-none transition-all duration-300 text-white placeholder-white/60"
                  />
                </motion.div>

                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  className="relative group"
                >
                  <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 group-hover:text-white/80 transition-colors" />
                  <input
                    type="text"
                    placeholder="City, state or remote"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white/10 border-2 border-white/20 rounded-xl focus:border-white/40 focus:ring-4 focus:ring-white/10 outline-none transition-all duration-300 text-white placeholder-white/60"
                  />
                </motion.div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-white text-blue-700 hover:bg-blue-50 py-4 rounded-xl font-semibold transition-all duration-300 text-lg"
                >
                  Search Jobs
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
